import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { UtensilsCrossed } from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "@/context/AuthContext";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

export default function Login() {
  const { login } = useAuth();
  const navigate = useNavigate();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [mode, setMode] = useState("login");
  const [forgotSent, setForgotSent] = useState(false);
  const [cms, setCms] = useState(null);
  useEffect(() => {
    api.get("/public/platform").then((r) => setCms(r.data.landing || {})).catch(() => {});
  }, []);

  const forgot = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await api.post("/auth/forgot-password", { email });
      setForgotSent(true);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      const u = await login(email, password);
      navigate(u.company_id ? "/dashboard" : u.platform_role === "super_admin" ? "/admin/vendors" : "/dashboard");
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="flex min-h-screen">
      <div className="hidden w-1/2 flex-col justify-between bg-ink p-12 text-white lg:flex">
        <div className="flex items-center gap-3">
          <div className="flex h-10 w-10 items-center justify-center bg-brand">
            <UtensilsCrossed className="h-5 w-5" />
          </div>
          <span className="font-heading text-lg font-bold tracking-tight" data-testid="login-brand">{cms === null ? "" : cms.login_brand || "Catering Diary"}</span>
          {cms?.login_tagline && <p className="text-[10px] font-medium uppercase tracking-[0.2em] text-white/50" data-testid="login-tagline">{cms.login_tagline}</p>}
        </div>
        <div>
          <h1 className="font-heading text-4xl font-black leading-[1.05] tracking-tight xl:text-5xl">
            Every unit. Every venue.
            <br />
            <span className="text-brand">One command center.</span>
          </h1>
          <p className="mt-6 max-w-md text-base text-white/60">
            CRM, fleet dispatch, venue logistics and subcontractor coordination for multi-unit event catering operations.
          </p>
        </div>
        <p className="text-xs uppercase tracking-[0.2em] text-white/40">Enquiries → Quotes → Dispatch → Settlement</p>
      </div>

      <div className="flex flex-1 items-center justify-center bg-background p-8">
        <div className="w-full max-w-sm">
          <h2 className="font-heading text-2xl font-bold tracking-tight" data-testid="login-title">
            Sign in
          </h2>
          <p className="mt-1 text-sm text-muted-foreground">
            Business &amp; super-admin access.
          </p>
          {mode === "forgot" ? (
            <form onSubmit={forgot} className="mt-8 space-y-5" data-testid="forgot-form">
              {forgotSent ? (
                <p data-testid="forgot-sent" className="border border-emerald-300 bg-emerald-50 px-3 py-2 text-sm text-emerald-800">If that email is registered, a reset link is on its way — check your inbox.</p>
              ) : (
                <>
                  <div className="space-y-2">
                    <Label htmlFor="forgot-email">Email</Label>
                    <Input data-testid="forgot-email-input" id="forgot-email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
                  </div>
                  {error && <p data-testid="forgot-error" className="border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700">{error}</p>}
                  <Button data-testid="forgot-submit-button" type="submit" disabled={loading} className="w-full rounded-md bg-ink hover:bg-ink-light">{loading ? "Sending…" : "Send reset link"}</Button>
                </>
              )}
              <button type="button" data-testid="forgot-back-button" onClick={() => { setMode("login"); setError(""); setForgotSent(false); }} className="text-sm font-medium text-brand hover:text-brand-dark">← Back to sign in</button>
            </form>
          ) : (
          <form onSubmit={submit} className="mt-8 space-y-5" data-testid="login-form">
            <div className="space-y-2">
              <Label htmlFor="email">Email</Label>
              <Input data-testid="login-email-input" id="email" type="email" value={email} onChange={(e) => setEmail(e.target.value)} required />
            </div>
            <div className="space-y-2">
              <div className="flex items-center justify-between">
                <Label htmlFor="password">Password</Label>
                <button type="button" data-testid="forgot-password-link" onClick={() => { setMode("forgot"); setError(""); }} className="text-xs font-medium text-brand hover:text-brand-dark">Forgot password?</button>
              </div>
              <Input data-testid="login-password-input" id="password" type="password" value={password} onChange={(e) => setPassword(e.target.value)} required />
            </div>
            {error && (
              <p data-testid="login-error" className="border border-red-300 bg-red-50 px-3 py-2 text-sm text-red-700">
                {error}
              </p>
            )}
            <Button data-testid="login-submit-button" type="submit" disabled={loading} className="w-full rounded-md bg-ink hover:bg-ink-light">
              {loading ? "Please wait…" : "Sign in"}
            </Button>
          </form>
          )}
          <a href="/" data-testid="back-to-landing" className="mt-6 inline-block text-sm font-medium text-brand hover:text-brand-dark">
            ← Back to CateringDiary
          </a>
        </div>
      </div>
    </div>
  );
}
